import { Link } from "react-router-dom";
import { Facebook, Instagram, Clock, MapPin, Phone, Mail, Spade } from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-black border-t border-white/10 relative z-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <Spade className="w-8 h-8 text-gold" />
              <span className="font-heading text-2xl text-white">
                <span className="text-gold">SMSC</span>
              </span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              San Marcos Social Club is the premier poker room in San Marcos, Texas. 
              Veteran owned and operated.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/OTRSanMarcos/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/10 transition-all"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/ontheriverpokerclubsm/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/10 transition-all"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.tiktok.com/@ontheriverpokerclubsm"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/10 transition-all"
                data-testid="footer-tiktok"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-lg text-white mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { to: "/tournaments", label: "Tournament Schedule" },
                { to: "/membership", label: "Membership" },
                { to: "/events", label: "Events" },
                { to: "/gallery", label: "Gallery" },
                { to: "/leaderboard", label: "Leaderboard" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-white/60 hover:text-gold text-sm transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading text-lg text-white mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <span className="text-white/40 text-sm italic">
                  Address Coming Soon
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gold shrink-0" />
                <span className="text-white/40 text-sm italic" data-testid="footer-phone">
                  Coming Soon
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gold shrink-0" />
                <span className="text-white/40 text-sm italic" data-testid="footer-email">
                  Coming Soon
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <span className="text-white/40 text-sm italic">
                  Hours Coming Soon
                </span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div>
            <h3 className="font-heading text-lg text-white mb-6">Join the Action</h3>
            <p className="text-white/60 text-sm mb-6">
              Ready to play? Visit us today and experience the best poker action in San Marcos!
            </p>
            <Link
              to="/contact"
              className="inline-block bg-gold text-black font-bold uppercase tracking-wider px-6 py-3 text-sm hover:bg-gold-dark transition-all duration-300 hover:scale-105 shadow-gold"
              data-testid="footer-cta"
            >
              Contact Us
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-white/40 text-sm">
              &copy; {new Date().getFullYear()} San Marcos Social Club. All rights reserved.
            </p>
            <span className="text-white/20 hidden sm:inline">|</span>
            <p className="text-white/40 text-sm italic">Coming Soon</p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/privacy-policy"
              className="text-white/40 text-sm hover:text-gold transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="text-white/20">|</span>
            <Link
              to="/terms-of-service"
              className="text-white/40 text-sm hover:text-gold transition-colors"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
            <span className="text-white/20">|</span>
            <Link
              to="/admin/login"
              className="text-white/30 text-xs hover:text-gold/60 transition-colors"
              data-testid="footer-admin-link"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
